import Pagination from '@mui/material/Pagination';
import Skeleton from '@mui/material/Skeleton';
import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Table as MuiTable,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  TableSortLabel,
  Paper,
  TableContainer,
  Divider,
} from '@mui/material';
import ComponentProps from 'store/types/ComponentProps';
import { SortConfig, SortDirection, TableColumn } from 'store/types/Table';
import Spinner from 'components/shared/Spinner';
import { getListByPage } from 'util/table';
import Row from './Row';

import commonStyles from 'styles/common.module.scss';
import styles from './Table.module.scss';

export interface TableRowProps<T> {
  selected: (record: T) => boolean;
}

interface TableProps<T> extends ComponentProps {
  list: T[];
  columns: Array<TableColumn<T>>;
  sort?: SortConfig<T>;
  onSortChange?: (sortConfig: SortConfig<T>) => void;
  rowProps?: TableRowProps<T>;
  itemsPerPage?: number;
  showPagination?: boolean;
  loading?: boolean;
  submitLoading?: boolean;
  cellClassName?: string;
  showChildren?: boolean;
  noResultsText?: string;
}

type TableComponentType<T = any> = React.FunctionComponent<TableProps<T>>;

const skeletonProps = {
  width: '100%',
  variant: 'text',
  className: styles.skeleton,
  ['data-testid']: 'table-skeleton',
};

const Table: TableComponentType = ({
  list,
  columns,
  sort,
  onSortChange,
  className = '',
  cellClassName = '',
  rowProps,
  itemsPerPage = 10,
  showPagination = false,
  loading = false,
  submitLoading = false,
  showChildren = false,
  noResultsText = 'No records to display',
}) => {
  const [page, setPage] = useState<number>(1);
  const pagesCount = useMemo(() => Math.ceil(list.length / itemsPerPage), [list.length, itemsPerPage]);
  const pageList = useMemo(
    () => (showPagination ? getListByPage(list, itemsPerPage, page) : list),
    [list, showPagination, itemsPerPage, page]
  );

  useEffect(() => {
    setPage(1);
  }, [list]);

  const handleSortChange = useCallback(
    (column: string, direction?: SortDirection) => () => {
      if (onSortChange) {
        onSortChange({ column, direction: direction === 'asc' ? 'desc' : 'asc' });
      }
    },
    [onSortChange]
  );

  const handlePageChange = useCallback((_: any, newPage: number) => {
    setPage(newPage);
  }, []);

  const headerCells: React.ReactNode[] = useMemo(
    () =>
      columns.map(({ hidden, dataIndex = '', label, align, sortable = false, key }) => {
        const hasSort: boolean = sortable && !!sort && sort.column === dataIndex;
        const sortDirection: SortDirection | undefined = hasSort ? sort && sort.direction : undefined;
        const columnName = dataIndex.toString() || key;
        const testId = `table-cell-${columnName}-header`;

        return (
          <TableCell
            align={align}
            key={testId}
            hidden={hidden}
            data-testid={testId}
            sortDirection={sortDirection || false}
            className={cellClassName}
          >
            <TableSortLabel
              active={hasSort}
              hideSortIcon={!sortable}
              direction={sortDirection}
              onClick={sortable && dataIndex ? handleSortChange(dataIndex.toString(), sortDirection) : undefined}
              data-testid={`${testId}-label`}
            >
              {label}
            </TableSortLabel>
          </TableCell>
        );
      }),
    [sort, cellClassName, handleSortChange, columns]
  );

  return loading && !submitLoading && !list.length ? (
    <>
      <Skeleton {...(skeletonProps as any)} />
      <Divider />
      <Skeleton {...(skeletonProps as any)} />
      <Divider />
      <Skeleton {...(skeletonProps as any)} />
      <Divider />
    </>
  ) : (
    <Spinner loading={submitLoading || loading}>
      <TableContainer component={Paper} className={classNames(commonStyles.card, className)} data-testid={'table'}>
        <MuiTable size={'medium'}>
          <TableHead>
            <TableRow data-testid={'table-row-header'}>
              {showChildren && <TableCell></TableCell>}
              {headerCells}
            </TableRow>
          </TableHead>
          <TableBody>
            {pageList.length ? (
              pageList.map((record: any, index: number) => (
                <Row
                  key={`table-row-${index}`}
                  record={record}
                  index={index}
                  columns={columns}
                  cellClassName={cellClassName}
                  rowProps={rowProps}
                  showChildren={showChildren}
                />
              ))
            ) : (
              <TableRow key={`no-data-row`}>
                <TableCell align={'center'} colSpan={columns.length} className={cellClassName}>
                  <div className={styles.noData} data-testid={'table-no-data'}>
                    {noResultsText}
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </MuiTable>
        {showPagination && (
          <div className={styles.paginationContainer}>
            <Pagination
              disabled={!list.length}
              showFirstButton={true}
              showLastButton={true}
              count={pagesCount}
              page={page}
              onChange={handlePageChange}
              data-testid={'table-pagination'}
            />
          </div>
        )}
      </TableContainer>
    </Spinner>
  );
};

export default Table;
